import { FC, useMemo } from "react";
import { TaskUpdateMarketInfo } from "tm-shared/types";

import { Loading } from "@/components/Loading";
import { StepActionButtonUi } from "@/components/StepActionButtonUi";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import useAsyncAction from "@/hooks/useAsyncAction";
import { useCompleteTask } from "@/hooks/useCompleteTask";
import { makeCommand, useMakeCommand } from "@/hooks/useRequestQueue";
import { setMdTasksConfig } from "@/store/slices/ui";
import { getPhotoIdsFromPhotoTask, isSubtaskCompleted } from "@/utils";

interface StepButtonProps {
    task: TaskUpdateMarketInfo;
    marketId: string;
    currentCompleted: boolean;
    currentStep: number;
}

export const StepButton: FC<StepButtonProps> = ({
    task,
    marketId,
    currentCompleted = false,
    currentStep,
}) => {
    const dispatch = useAppDispatch();

    const taskId = task.id;
    const subtaskIds = task.context.subtasks.map((t) => t.subtaskId);

    const payloadMapBySubtaskId = useAppSelector(
        (s) => s.progress.updateMarketInfo[taskId],
    );

    const allCompleted = useMemo(() => {
        if (!payloadMapBySubtaskId) {
            return false;
        }

        const allCompleted = Object.values(payloadMapBySubtaskId).every(
            isSubtaskCompleted,
        );

        return allCompleted;
    }, [payloadMapBySubtaskId]);

    const activeIndex = currentStep;
    const isLast = activeIndex === subtaskIds.length - 1;
    const buttonDisabled = isLast && !allCompleted && currentCompleted;

    const onNext = () => {
        if (isLast) return;

        dispatch(
            setMdTasksConfig({
                marketId,
                taskId,
                field: "activeItem",
                value: String(activeIndex + 1),
            }),
        );
    };

    const { makeTaskCompleteCommands, makeUploadPhotoCommands } =
        useMakeCommand();

    const completeTask = useCompleteTask({ marketId, taskId });

    const [handleComplete, completeLoading] = useAsyncAction(async () => {
        const photoIds = getPhotoIdsFromPhotoTask(payloadMapBySubtaskId);

        const hasPhoto = photoIds.length > 0;

        const taskCompleteCommands = await makeTaskCompleteCommands({
            params: { taskId, hasPhoto, payload: payloadMapBySubtaskId },
            marketId,
        });

        const commands = [
            ...makeUploadPhotoCommands(photoIds),

            ...taskCompleteCommands,

            makeCommand({
                type: "updatePhotoStorage",
                title: "Синхронізація сховища",
                params: photoIds,
                options: {
                    stopOnError: false,
                },
            }),
        ];

        completeTask({ commands });
    });

    return (
        <>
            {currentCompleted && (
                <StepActionButtonUi
                    onNext={onNext}
                    onFinish={handleComplete}
                    isLast={isLast}
                    disabled={buttonDisabled}
                    loading={completeLoading}
                />
            )}

            {completeLoading && <Loading title="Завершення задачі" />}
        </>
    );
};
